import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, ChevronDown, X, Search, Zap, Loader2, Sparkles, ScanLine } from "lucide-react";
import { API } from "./shopData";
import { formatCarModel } from "./formatCarModel";

// Populaire automerken — 1-klik selectie chips
const POPULAR_MAKES = [
  "Volkswagen", "Audi", "BMW", "Mercedes-Benz", "Ford", "Opel",
  "Renault", "Peugeot", "Toyota", "Honda", "Nissan", "Subaru", "Mitsubishi",
];

/**
 * VehicleSelector — Universal voertuig picker voor TTW Shop.
 *
 * Twee varianten via `variant` prop:
 *  - "hero"   → grote panel bovenaan de shop (initial view)
 *  - "sticky" → compacte balk die verschijnt zodra klant een auto heeft gekozen
 *
 * Onthoudt keuze via de bovenliggende `savedCar` (localStorage) — reeds
 * geregeld in Shop.jsx. Deze component is puur presentational + fetch.
 */
export default function VehicleSelector({
  variant = "hero",
  make = "",
  model = "",
  engineCode = "",
  rdwInfo = null,
  engineCodeSuggestions = [],
  engineSwap = false,
  onApply,
  onClear,
  onSelectEngineCode,
  onClearEngineCode,
  onToggleEngineSwap,
}) {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  const [localMake, setLocalMake] = useState(make || "");
  const [localModel, setLocalModel] = useState(model || "");
  const [localEngine, setLocalEngine] = useState(engineCode || "");
  const [engineQuery, setEngineQuery] = useState(engineCode || "");
  const [engineFocus, setEngineFocus] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingEngines, setLoadingEngines] = useState(false);
  const engineBoxRef = useRef(null);
  const [kenteken, setKenteken] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null);

  // Sync met props (bewaarde auto uit localStorage)
  useEffect(() => {
    setLocalMake(make || "");
    setLocalModel(model || "");
    setLocalEngine(engineCode || "");
    setEngineQuery(engineCode || "");
  }, [make, model, engineCode]);

  // Fetch alle merken (met product-count)
  useEffect(() => {
    axios.get(`${API}/products/car-makes`).then((r) => setMakes(r.data || [])).catch(() => {});
  }, []);

  // Fetch modellen wanneer merk verandert
  useEffect(() => {
    if (!localMake) { setModels([]); return; }
    setLoadingModels(true);
    axios.get(`${API}/products/car-models`, { params: { make: localMake } })
      .then((r) => setModels(r.data || []))
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [localMake]);

  // Fetch motorcodes wanneer merk (+ evt. model) verandert
  useEffect(() => {
    if (!localMake) { setEngines([]); return; }
    setLoadingEngines(true);
    axios.get(`${API}/products/engine-codes`, { params: { car_make: localMake } })
      .then((r) => setEngines(r.data || []))
      .catch(() => setEngines([]))
      .finally(() => setLoadingEngines(false));
  }, [localMake]);

  const filteredEngines = useMemo(() => {
    const q = (engineQuery || "").trim().toUpperCase();
    if (!q) return engines.slice(0, 80);
    return engines.filter((e) => (e.engine || "").toUpperCase().includes(q)).slice(0, 80);
  }, [engines, engineQuery]);

  // Sluit motorcode-dropdown bij klik buiten
  useEffect(() => {
    const onDocClick = (e) => {
      if (engineBoxRef.current && !engineBoxRef.current.contains(e.target)) {
        setEngineFocus(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const popularMakesAvailable = useMemo(() => {
    const set = new Set(makes.map((m) => (m.make || "").toLowerCase()));
    return POPULAR_MAKES.filter((m) => set.has(m.toLowerCase()));
  }, [makes]);

  const handleMakeChange = (val) => {
    setLocalMake(val);
    setLocalModel("");
    setLocalEngine("");
    setEngineQuery("");
  };

  const handleApply = () => {
    if (!localMake) return;
    onApply?.({
      make: localMake,
      model: localModel || null,
      engineCode: localEngine || null,
    });
  };

  const handleClearAll = () => {
    setLocalMake("");
    setLocalModel("");
    setLocalEngine("");
    setEngineQuery("");
    setKenteken("");
    setLookupInfo(null);
    onClear?.();
  };

  // ─── RDW Kenteken lookup ───
  const doKentekenLookup = async () => {
    const clean = (kenteken || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length !== 6) {
      toast.error("Voer een Nederlands kenteken van 6 tekens in");
      return;
    }
    setLookupLoading(true);
    setLookupInfo(null);
    try {
      const { data } = await axios.get(`${API}/vehicle/lookup`, { params: { kenteken: clean } });
      setLookupInfo(data);
      if (!data.match?.matched || !data.match.car_make) {
        toast.warning(`${data.rdw.merk} ${data.rdw.handelsbenaming} gevonden maar niet in onze catalogus.`);
        return;
      }
      const foundMake = data.match.car_make;
      // BEWUST NIET het catalogus-model forceren — de exacte auto van de klant wordt
      // getoond in het lookup-panel; we filteren de shop breed op MERK zodat er altijd
      // producten zichtbaar zijn. Klant kan zelf verfijnen op motorcode (zie chips).
      setLocalMake(foundMake);
      setLocalModel("");

      // Slimme auto-selectie: als de top motorcode suggestie een cc-match of year-match heeft,
      // pas hem direct toe MET swap-modus zodat de klant meteen:
      //   - engine-parts ziet die passen op het gevonden motorblok
      //   - chassis-parts (remmen, ophanging) ziet die op het merk passen
      const suggestions = data.match.engine_code_suggestions || [];
      const bestSuggestion = suggestions[0];
      const isConfident = bestSuggestion && (
        (bestSuggestion.cc_matches || 0) > 0 ||
        (bestSuggestion.year_matches || 0) > 0
      );
      const autoEngineCode = isConfident ? bestSuggestion.engine : null;
      const autoSwap = Boolean(autoEngineCode);

      setLocalEngine(autoEngineCode || "");
      setEngineQuery(autoEngineCode || "");
      const rdwLabel = `${data.rdw.merk} ${data.rdw.handelsbenaming}${data.rdw.bouwjaar ? ` (${data.rdw.bouwjaar})` : ""}`;
      const engineHint = autoEngineCode
        ? ` — motorcode ${autoEngineCode} + chassis-onderdelen`
        : (suggestions.length ? ` — ${suggestions.length} motorcodes beschikbaar` : "");
      toast.success(`${rdwLabel} — ${data.rdw.brandstof || ""}${engineHint}`);
      onApply?.({
        make: foundMake,
        model: null,
        engineCode: autoEngineCode,
        engineSwap: autoSwap,
        rdw: data.rdw,
        engineCodeSuggestions: suggestions,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Kenteken lookup mislukt";
      toast.error(detail);
    } finally {
      setLookupLoading(false);
    }
  };

  // Format kenteken input met streepjes voor visuele feedback (NL formaat)
  const formatKentekenDisplay = (v) => {
    const c = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    // Simpel: 2-2-2 patroon voor 6 chars
    if (c.length <= 2) return c;
    if (c.length <= 4) return `${c.slice(0, 2)}-${c.slice(2)}`;
    return `${c.slice(0, 2)}-${c.slice(2, 4)}-${c.slice(4, 6)}`;
  };

  // ─── STICKY VARIANT ───
  if (variant === "sticky") {
    if (!make) return null;
    // Exacte RDW-auto weergave: toont het handelsbenaming van RDW (niet het catalog-model)
    const rdwLabel = rdwInfo?.handelsbenaming ? rdwInfo.handelsbenaming : (model ? formatCarModel(model, make).display : "");
    const specParts = [];
    if (rdwInfo?.cilinderinhoud_l) specParts.push(`${rdwInfo.cilinderinhoud_l}L`);
    if (rdwInfo?.brandstof) specParts.push(rdwInfo.brandstof);
    if (rdwInfo?.vermogen_pk) specParts.push(`${rdwInfo.vermogen_pk} pk`);
    if (rdwInfo?.bouwjaar) specParts.push(String(rdwInfo.bouwjaar));
    return (
      <div
        data-testid="vehicle-selector-sticky"
        className="sticky top-16 z-30 -mx-6 mb-4 bg-gradient-to-r from-[#1a0a0a] via-[#0f0f0f] to-[#1a0a0a] border-y border-[#FF3B30]/25 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 flex-wrap">
          <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/15 border border-[#FF3B30]/30 flex items-center justify-center flex-shrink-0">
            <Car size={14} className="text-[#FF3B30]" />
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">Actieve auto:</span>
            <span className="text-white text-sm font-semibold" data-testid="active-vehicle-make">{rdwInfo?.merk || make}</span>
            {rdwLabel && (
              <>
                <span className="text-[#333]">·</span>
                <span className="text-white text-sm" data-testid="active-vehicle-model" title={model || rdwLabel}>
                  {rdwLabel}
                </span>
              </>
            )}
            {specParts.length > 0 && (
              <span className="text-[10px] text-[#888] tracking-wide" data-testid="active-vehicle-specs">
                {specParts.join(" · ")}
              </span>
            )}
            {engineCode && (
              <>
                <span className="text-[#333]">·</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F1C40F]/10 border border-[#F1C40F]/25 rounded text-[#F1C40F] font-mono text-xs" data-testid="active-vehicle-engine">
                  <Zap size={10} /> {engineCode}
                  {onClearEngineCode && (
                    <button
                      onClick={onClearEngineCode}
                      className="ml-1 hover:text-white"
                      data-testid="clear-engine-code"
                      title="Motorcode filter verwijderen"
                    >
                      <X size={11} />
                    </button>
                  )}
                </span>
                {onToggleEngineSwap && (
                  <button
                    onClick={onToggleEngineSwap}
                    data-testid="engine-swap-toggle"
                    title={engineSwap ? "Alle onderdelen voor deze auto (chassis + motor)" : "Alleen onderdelen die STRIKT bij motorcode passen"}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border transition-all ${engineSwap ? "bg-[#27AE60]/15 border-[#27AE60]/40 text-[#27AE60]" : "bg-[#F1C40F]/15 border-[#F1C40F]/40 text-[#F1C40F]"}`}
                  >
                    <Sparkles size={10} /> {engineSwap ? "Alle onderdelen" : "Alleen motorcode"}
                  </button>
                )}
              </>
            )}
          </div>
          <button
            onClick={handleClearAll}
            className="text-xs font-semibold text-[#888] hover:text-[#FF3B30] px-2 py-1 rounded transition-colors flex items-center gap-1"
            data-testid="vehicle-selector-clear-sticky"
          >
            <X size={13} /> Wissel auto
          </button>
        </div>
        {/* Motorcode suggesties + handmatige invoer — altijd zichtbaar zodra een auto actief is */}
        {(!engineCode && engineCodeSuggestions.length > 0) && (
          <div className="max-w-7xl mx-auto px-6 pb-2 flex items-center gap-2 flex-wrap" data-testid="engine-code-suggestions">
            <span className="text-[9px] uppercase tracking-widest text-[#F1C40F] font-bold">
              <Zap size={9} className="inline mr-0.5" />Motorcode voor exacte fitment:
            </span>
            {engineCodeSuggestions.slice(0, 12).map((s) => (
              <button
                key={s.engine}
                onClick={() => onSelectEngineCode?.(s.engine)}
                data-testid={`engine-code-suggestion-${s.engine.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="px-2 py-0.5 bg-[#0A0A0A] border border-[#F1C40F]/25 rounded-md text-[10px] font-mono text-[#F1C40F] hover:bg-[#F1C40F]/10 hover:border-[#F1C40F]/50 transition-all"
              >
                {s.engine} <span className="text-[#666] font-sans">({s.count})</span>
              </button>
            ))}
            {engineCodeSuggestions.length > 12 && (
              <span className="text-[10px] text-[#555]">+{engineCodeSuggestions.length - 12} meer</span>
            )}
            {/* Handmatige motorcode-invoer (voor swaps of onbekende blokken) */}
            <SwapMotorcodeSearch onSelect={onSelectEngineCode} label="Eigen motorcode" />
          </div>
        )}
        {/* Als GEEN suggesties beschikbaar zijn maar wel een auto: toon alleen typeahead */}
        {!engineCode && engineCodeSuggestions.length === 0 && onSelectEngineCode && (
          <div className="max-w-7xl mx-auto px-6 pb-2 flex items-center gap-2 flex-wrap" data-testid="engine-code-manual">
            <span className="text-[9px] uppercase tracking-widest text-[#F1C40F] font-bold">
              <Zap size={9} className="inline mr-0.5" />Motorblok kiezen:
            </span>
            <SwapMotorcodeSearch onSelect={onSelectEngineCode} label="Motorcode invoeren" openByDefault />
            <span className="text-[10px] text-[#555]">bv. BWA, K20A2, 2JZ-GTE, VR6...</span>
          </div>
        )}
        {/* Bij actieve motorcode: altijd optie om motorcode te wisselen (voor swap of typefout) */}
        {engineCode && (
          <div className="max-w-7xl mx-auto px-6 pb-2 text-[10px] flex items-center gap-2 flex-wrap" data-testid="engine-code-context">
            {engineSwap ? (
              <span className="text-[#27AE60] flex items-center gap-1"><Sparkles size={10} /> Chassis-onderdelen ({make}) + motoronderdelen voor {engineCode}</span>
            ) : (
              <span className="text-[#F1C40F] flex items-center gap-1"><Zap size={10} /> Strikt: alleen onderdelen die exact matchen met motorcode {engineCode}</span>
            )}
            <SwapMotorcodeSearch onSelect={onSelectEngineCode} label="Andere motorcode" />
          </div>
        )}
      </div>
    );
  }

  // ─── HERO VARIANT ───
  const selectCls = "w-full bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-3 text-sm text-white appearance-none focus:border-[#FF3B30]/60 focus:outline-none transition-colors cursor-pointer";
  const inputCls = "w-full bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-3 text-sm text-white placeholder:text-[#444] focus:border-[#FF3B30]/60 focus:outline-none transition-colors";

  return (
    <div
      data-testid="vehicle-selector-hero"
      className="relative mb-6 bg-gradient-to-br from-[#1a0a0a] via-[#0f0f0f] to-[#0a0f14] border border-[#FF3B30]/25 rounded-2xl p-5 sm:p-6 overflow-hidden"
    >
      {/* Subtle grid backdrop */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "linear-gradient(#FF3B30 1px, transparent 1px), linear-gradient(90deg, #FF3B30 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={16} className="text-[#FF3B30]" />
          <h2 className="text-white font-bold text-sm uppercase tracking-widest">Vind snel onderdelen voor jouw auto</h2>
        </div>
        <p className="text-[#888] text-xs mb-5">
          Kies je merk, model &amp; motorcode — wij tonen alleen onderdelen die exact passen.
        </p>

        {/* Kenteken lookup — RDW open data */}
        <div className="mb-5 bg-[#0a0a0a] border border-[#1E1E1E] rounded-xl p-3 sm:p-4" data-testid="kenteken-panel">
          <div className="flex items-center gap-2 mb-2">
            <ScanLine size={13} className="text-[#F1C40F]" />
            <span className="text-[10px] text-[#F1C40F] uppercase tracking-widest font-bold">Sneller — vul je kenteken in</span>
            <span className="text-[9px] text-[#555]">Data via RDW</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 max-w-xs">
              {/* NL kenteken plaat-styling */}
              <div className="flex bg-[#F1C40F] rounded-md overflow-hidden border border-[#F1C40F]/40 focus-within:ring-2 focus-within:ring-[#F1C40F]/40">
                <div className="w-6 bg-[#2464A4] flex flex-col items-center justify-center text-white text-[8px] font-bold py-1">
                  <span>NL</span>
                </div>
                <input
                  type="text"
                  value={formatKentekenDisplay(kenteken)}
                  onChange={(e) => setKenteken(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doKentekenLookup(); }}
                  placeholder="XX-XX-XX"
                  maxLength={9}
                  autoComplete="off"
                  className="flex-1 bg-[#F1C40F] text-[#111] placeholder:text-[#111]/40 font-bold text-center text-lg tracking-widest py-1.5 px-2 outline-none uppercase"
                  data-testid="kenteken-input"
                  style={{ fontFamily: "'Kenteken', 'Arial Black', sans-serif", letterSpacing: "0.15em" }}
                />
              </div>
            </div>
            <button
              onClick={doKentekenLookup}
              disabled={lookupLoading || (kenteken || "").replace(/[^A-Z0-9]/gi, "").length !== 6}
              data-testid="kenteken-lookup"
              className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${lookupLoading || (kenteken || "").replace(/[^A-Z0-9]/gi, "").length !== 6 ? "bg-[#1a1a1a] text-[#555] cursor-not-allowed" : "bg-[#F1C40F] text-[#111] hover:bg-[#F5D33E]"}`}
            >
              {lookupLoading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
              Zoek voertuig
            </button>
          </div>
          {lookupInfo?.rdw && (
            <div className="mt-3 text-[11px] text-[#ccc] flex flex-wrap items-center gap-2" data-testid="kenteken-result">
              <span className="text-[#888]">Gevonden:</span>
              <span className="text-white font-semibold">{lookupInfo.rdw.merk} {lookupInfo.rdw.handelsbenaming}</span>
              {lookupInfo.rdw.cilinderinhoud_l && (
                <span className="text-[#F1C40F]">{lookupInfo.rdw.cilinderinhoud_l}L</span>
              )}
              {lookupInfo.rdw.brandstof && (
                <span className="px-1.5 py-0.5 rounded bg-[#111] border border-[#2a2a2a] text-[10px]">{lookupInfo.rdw.brandstof}</span>
              )}
              {lookupInfo.rdw.vermogen_pk && (
                <span className="text-[#888]">{lookupInfo.rdw.vermogen_pk} pk</span>
              )}
              {lookupInfo.rdw.bouwjaar && (
                <span className="text-[#888]">· {lookupInfo.rdw.bouwjaar}</span>
              )}
              {!lookupInfo.match?.matched && (
                <span className="text-[#E74C3C] text-[10px]">(niet in catalogus)</span>
              )}
            </div>
          )}
        </div>

        {/* Populaire merk chips */}
        {popularMakesAvailable.length > 0 && (
          <div className="mb-4 flex gap-2 flex-wrap" data-testid="popular-makes">
            {popularMakesAvailable.map((brand) => {
              const item = makes.find((m) => (m.make || "").toLowerCase() === brand.toLowerCase());
              const active = localMake.toLowerCase() === brand.toLowerCase();
              return (
                <button
                  key={brand}
                  onClick={() => handleMakeChange(item?.make || brand)}
                  data-testid={`popular-make-${brand.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all whitespace-nowrap ${active ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-[#0d0d0d] text-[#aaa] border-[#1E1E1E] hover:border-[#FF3B30]/50 hover:text-white"}`}
                >
                  {brand}
                  {item && <span className="ml-1.5 opacity-60 font-normal">({item.count})</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Dropdowns rij */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Merk */}
          <div className="relative md:col-span-1">
            <label className="block text-[10px] text-[#FF3B30] uppercase tracking-widest font-bold mb-1.5">Automerk</label>
            <select
              value={localMake}
              onChange={(e) => handleMakeChange(e.target.value)}
              className={selectCls}
              data-testid="vs-make"
            >
              <option value="">Kies merk...</option>
              {makes.map((m) => (
                <option key={m.make} value={m.make}>{`${m.make} (${m.count})`}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 bottom-3.5 text-[#555] pointer-events-none" />
          </div>

          {/* Model */}
          <div className="relative md:col-span-1">
            <label className="block text-[10px] text-[#FF3B30] uppercase tracking-widest font-bold mb-1.5">Model</label>
            <select
              value={localModel}
              onChange={(e) => setLocalModel(e.target.value)}
              disabled={!localMake || loadingModels}
              className={`${selectCls} ${!localMake ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="vs-model"
            >
              <option value="">{loadingModels ? "Laden..." : localMake ? "Alle modellen" : "Eerst merk kiezen"}</option>
              {models.slice(0, 200).map((m) => {
                const { display, details } = formatCarModel(m.model, localMake);
                const label = details ? `${display} · ${details}` : display;
                return (
                  <option key={m.model} value={m.model}>
                    {`${label} (${m.count})`}
                  </option>
                );
              })}
            </select>
            <ChevronDown size={14} className="absolute right-3 bottom-3.5 text-[#555] pointer-events-none" />
          </div>

          {/* Motorcode — typeahead */}
          <div className="relative md:col-span-1" ref={engineBoxRef}>
            <label className="block text-[10px] text-[#FF3B30] uppercase tracking-widest font-bold mb-1.5">Motorcode</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
              <input
                type="text"
                value={engineQuery}
                onChange={(e) => { setEngineQuery(e.target.value.toUpperCase()); setLocalEngine(""); setEngineFocus(true); }}
                onFocus={() => setEngineFocus(true)}
                disabled={!localMake}
                placeholder={localMake ? (loadingEngines ? "Laden..." : "Bijv. BWA, EJ20T...") : "Eerst merk kiezen"}
                className={`${inputCls} pl-9 ${!localMake ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="vs-engine-input"
                autoComplete="off"
              />
              {(localEngine || engineQuery) && (
                <button
                  onClick={() => { setLocalEngine(""); setEngineQuery(""); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666] hover:text-white p-0.5"
                  data-testid="vs-engine-clear"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {engineFocus && localMake && filteredEngines.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-[#0A0A0A] border border-[#2a2a2a] rounded-lg shadow-2xl" data-testid="vs-engine-dropdown">
                {filteredEngines.map((e) => (
                  <button
                    key={e.engine}
                    onClick={() => { setLocalEngine(e.engine); setEngineQuery(e.engine); setEngineFocus(false); }}
                    className={`w-full text-left px-3 py-2 text-xs font-mono hover:bg-[#FF3B30]/10 hover:text-white transition-colors flex items-center justify-between ${localEngine === e.engine ? "bg-[#FF3B30]/15 text-[#FF3B30]" : "text-[#ccc]"}`}
                    data-testid={`vs-engine-option-${e.engine.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span>{e.engine}</span>
                    <span className="text-[#555] font-sans text-[10px]">{e.count} producten</span>
                  </button>
                ))}
              </div>
            )}
            {loadingEngines && localMake && (
              <Loader2 size={12} className="absolute right-8 top-9 animate-spin text-[#555]" />
            )}
          </div>

          {/* Apply knop */}
          <div className="md:col-span-1 flex flex-col justify-end">
            <button
              onClick={handleApply}
              disabled={!localMake}
              data-testid="vs-apply"
              className={`w-full py-3 rounded-lg font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${localMake ? "bg-[#FF3B30] text-white hover:bg-[#E0342A] hover:shadow-[0_0_20px_rgba(255,59,48,0.35)]" : "bg-[#1a1a1a] text-[#555] cursor-not-allowed"}`}
            >
              <Search size={14} /> Vind onderdelen
            </button>
            {localMake && (
              <button
                onClick={handleClearAll}
                className="text-[10px] text-[#666] hover:text-white mt-2 transition-colors"
                data-testid="vs-clear-hero"
              >
                Selectie wissen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component: type-ahead om ELKE motorcode uit de catalogus te zoeken ───
// (voor motorswap-scenario's waar het originele blok is vervangen)
function SwapMotorcodeSearch({ onSelect, label = "Andere motorcode?", openByDefault = false }) {
  const [open, setOpen] = useState(openByDefault);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  // Zoek in alle motorcodes zodra query >= 2 chars is
  useEffect(() => {
    if (!open || query.length < 2) { setResults([]); return; }
    setLoading(true);
    const controller = new AbortController();
    axios.get(`${API}/products/engine-codes`, { signal: controller.signal })
      .then((r) => {
        const q = query.toUpperCase();
        const all = r.data || [];
        setResults(all.filter((e) => (e.engine || "").toUpperCase().includes(q)).slice(0, 30));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, query]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative inline-block" ref={boxRef}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="swap-motorcode-open"
          className="px-2 py-0.5 bg-transparent border border-dashed border-[#F1C40F]/30 rounded-md text-[10px] text-[#F1C40F] hover:bg-[#F1C40F]/5 transition-all"
        >
          + {label}
        </button>
      ) : (
        <>
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              placeholder="Bv. VR6, K20A, 2JZ..."
              className="pl-6 pr-2 py-1 bg-[#0A0A0A] border border-[#F1C40F]/40 rounded-md text-[10px] font-mono text-white placeholder:text-[#555] focus:border-[#F1C40F] focus:outline-none w-40"
              data-testid="swap-motorcode-input"
            />
          </div>
          {loading && <Loader2 size={11} className="absolute right-1 top-1.5 animate-spin text-[#555]" />}
          {query.length >= 2 && results.length > 0 && (
            <div className="absolute z-40 top-full mt-1 max-h-52 min-w-40 overflow-y-auto bg-[#0A0A0A] border border-[#F1C40F]/30 rounded-md shadow-xl">
              {results.map((r) => (
                <button
                  key={r.engine}
                  onClick={() => { onSelect?.(r.engine); setOpen(false); setQuery(""); }}
                  data-testid={`swap-motorcode-option-${r.engine.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="w-full text-left px-2 py-1 text-[10px] font-mono text-[#ccc] hover:bg-[#F1C40F]/10 hover:text-[#F1C40F] flex items-center justify-between"
                >
                  <span>{r.engine}</span>
                  <span className="text-[#555] font-sans">{r.count}</span>
                </button>
              ))}
            </div>
          )}
          {query.length >= 2 && !loading && results.length === 0 && (
            <div className="absolute z-40 top-full mt-1 px-2 py-1 bg-[#0A0A0A] border border-[#2a2a2a] rounded-md text-[10px] text-[#666]">Geen motorcode gevonden</div>
          )}
        </>
      )}
    </div>
  );
}
